import type { CollectionSummaryJSONObject, FormSummaryJSONObject } from '@ikegps/core-api';
import type { IkeCollectionUpgradeFailureReplace } from '@ikegps/core-shared';
import { action, computed, makeObservable } from 'mobx';
import { InputState } from '../../components/input/input.state';
import { ModalFormComponent, type ModalFormComponentCtx } from '../../components/modal.form/modal.form.component';
import { NotificationService } from '../../components/notification.toast/notification.service';
import { Spinner } from '../../components/spinner/spinner';
import { Table } from '../../components/table/table';
import { addStyles } from '../../components/theme';
import { T, Translate } from '../../i18n';
import { m, type MithrilVNode } from '../../mithril';
import { Logger, Services } from '../../services';
import { AppStore } from '../../store';
import { CollectionFormModalCtrl, type CollectionStat } from './collection.form.modal.ctrl';
import { CSS, Style } from './collection.form.modal.style';

addStyles(Style);

export class CollectionFormModal extends ModalFormComponent {
	private ctrl: CollectionFormModalCtrl;
	private departmentForms: FormSummaryJSONObject[] = [];
	private errorState: InputState;
	private loadingState: InputState;

	constructor(ctx: ModalFormComponentCtx, collections: CollectionSummaryJSONObject[]) {
		super(ctx);
		makeObservable(this);
		this.ctrl = new CollectionFormModalCtrl(collections);

		this.errorState = new InputState(T.ERROR);
		this.loadingState = new InputState(T.LOADING);
		this.addState(this.errorState);
		this.addState(this.loadingState);

		AppStore.FormSummary.refresh();
	}

	get nodes(): MithrilVNode {
		this.departmentForms = AppStore.FormSummary.all;

		if (this.departmentForms.length === 0) {
			this.loadingState.setError(T.LOADING);
			return Spinner.view();
		}

		this.loadingState.reset();

		// If warnings exist, show warning UI
		if (this.ctrl.hasWarnings) {
			return this.viewWarning();
		}

		return m('div', { className: CSS.container }, [
			m('div', { className: CSS.content }, [this.errorsTable]),
			m('div', { className: CSS.content }, [this.viewStats()]),
		]);
	}

	@computed get errorsTable(): MithrilVNode {
		if (this.ctrl.failures.length === 0) {
			return null;
		}

		const title = Translate(T.COLLECTION_UPDATE_FORM_ERROR_TITLE, { count: this.ctrl.failures.length });
		const headers = [
			Translate(T.COLLECTION_TITLE),
			Translate(T.COLLECTION_UPDATE_FORM_REASON),
			Translate(T.COLLECTION_UPDATE_FORM_FIELD_NAME),
			Translate(T.COLLECTION_UPDATE_FORM_VALUE),
		];
		const rows: MithrilVNode[][] = [];

		this.ctrl.failures.forEach((error) => {
			let values = '';
			if ((<IkeCollectionUpgradeFailureReplace>error.failure).values) {
				const failure = error.failure as IkeCollectionUpgradeFailureReplace;
				values = failure.values.toString();
			}

			const reason = {
				missing: Translate(T.COLLECTION_UPDATE_FORM_FIELD_MISSING),
				replace: Translate(T.COLLECTION_UPDATE_FORM_FIELD_VALUE_MISSING),
			};

			rows.push([
				m('span', { title: error.collectionTitle }, error.collectionTitle),
				m('span', { title: reason[error.failure.reason] }, reason[error.failure.reason]),
				m(
					'span',
					{ title: error.failure.fieldName },
					error.failure.fieldName ? error.failure.fieldName : error.failure.fieldId
				),
				m('span', { title: values }, values),
			]);
		});

		return Table.view(title, headers, rows);
	}

	private viewStats(): MithrilVNode {
		const collectionStats: CollectionStat[] = this.ctrl.getCollectionStats(this.departmentForms);
		const title = Translate(T.COLLECTION_UPDATE_FORM_CONFIRM, { count: this.ctrl.collections.length });
		const headers = [
			Translate(T.COLLECTION_TITLE),
			Translate(T.COLLECTION_UPDATE_FORM_CURRENT_REVISION),
			Translate(T.COLLECTION_UPDATE_FORM_LATEST_REVISION),
			Translate(T.FORM_NAME),
		];
		const rows: MithrilVNode[][] = [];

		collectionStats.forEach((stat) => {
			const latestFormDate = stat.latestFormDate ? Services.DateTime.formatDateTime(stat.latestFormDate) : '';
			const latestFormField = stat.latestFormRevision ? latestFormDate : Translate(T.FORM_NOT_FOUND);
			const actualFormDate = stat.actualFormDate ? Services.DateTime.formatDateTime(stat.actualFormDate) : '';
			const actualFormField =
				stat.latestFormRevision === stat.actualFormRevision
					? Translate(T.COLLECTION_UPDATE_HAS_LATEST_FORM)
					: actualFormDate;
			rows.push([
				m('span', { title: stat.collectionTitle }, stat.collectionTitle),
				m('span', { title: actualFormField }, actualFormField),
				m('span', { title: latestFormField }, latestFormField),
				m('span', { title: stat.latestFormName }, stat.latestFormName),
			]);
		});

		return Table.view(title, headers, rows);
	}

	override failToChange() {
		// If warnings were triggered, hide the error notification and don't show error state
		if (this.ctrl.hasWarnings) {
			NotificationService.hide();
			return;
		}
		this.errorState.setError(T.ERROR);
	}

	override async submit(): Promise<void> {
		await this.ctrl.updateFormCollections(false);

		// If warnings were detected, switch to warning view and prevent success flow
		if (this.ctrl.hasWarnings) {
			this.displayCustomButtons = true;
			// Throw to prevent success notification and modal close, but this error
			// won't show a notification because we return early from failToChange
			throw new Error('SHOW_WARNING');
		}
	}

	private viewWarning(): MithrilVNode {
		return m('div', { className: CSS.container }, [
			m('div', { className: CSS.warningContainer }, [
				m('div', { className: CSS.warningTitle }, Translate(T.COLLECTION_UPDATE_FORM_WARNING_TITLE)),
				m('div', { className: CSS.warningBody }, Translate(T.COLLECTION_UPDATE_FORM_WARNING_BODY)),
				m('div', { className: CSS.warningList }, [
					m('div', Translate(T.COLLECTION_MIGRATE_FORM_BODY_WARNING_DESCRIPTION_PT2)),
					m('ul', [
						m('li', Translate(T.COLLECTION_MIGRATE_FORM_BODY_WARNING_DESCRIPTION_PT3)),
						m('li', Translate(T.COLLECTION_MIGRATE_FORM_BODY_WARNING_DESCRIPTION_PT4)),
					]),
				]),
			]),
			m('div', { className: CSS.content }, [this.warningsTable]),
			m('div', { className: CSS.content }, [this.viewStats()]),
		]);
	}

	@computed get warningsTable(): MithrilVNode {
		if (this.ctrl.warnings.length === 0) {
			return null;
		}

		const title = Translate(T.COLLECTION_UPDATE_FORM_WARNING_TABLE_TITLE, { count: this.ctrl.warnings.length });
		const headers = [
			Translate(T.COLLECTION_TITLE),
			Translate(T.COLLECTION_UPDATE_FORM_REASON),
			Translate(T.COLLECTION_UPDATE_FORM_FIELD_NAME),
			Translate(T.COLLECTION_UPDATE_FORM_VALUE),
		];
		const rows: MithrilVNode[][] = [];

		this.ctrl.warnings.forEach((warning) => {
			let values = '';
			if ((<IkeCollectionUpgradeFailureReplace>warning.failure).values) {
				const failure = warning.failure as IkeCollectionUpgradeFailureReplace;
				values = failure.values.toString();
			}

			const reason = {
				missing: Translate(T.COLLECTION_UPDATE_FORM_FIELD_MISSING),
				replace: Translate(T.COLLECTION_UPDATE_FORM_FIELD_VALUE_MISSING),
			};

			rows.push([
				m('span', { title: warning.collectionTitle }, warning.collectionTitle),
				m('span', { title: reason[warning.failure.reason] }, reason[warning.failure.reason]),
				m(
					'span',
					{ title: warning.failure.fieldName },
					warning.failure.fieldName ? warning.failure.fieldName : warning.failure.fieldId
				),
				m('span', { title: values }, values),
			]);
		});

		return Table.view(title, headers, rows);
	}

	protected override customButtons(): MithrilVNode[] {
		return [
			m(
				'button',
				{
					className: 'mdl-button',
					onclick: () => this.close(),
					type: 'button',
					disabled: this.loading,
				},
				Translate(T.COLLECTION_UPDATE_FORM_CANCEL)
			),
			m(
				'button',
				{
					className: 'mdl-button mdl-button--accent',
					onclick: () => this.proceedWithWarnings(),
					type: 'button',
					disabled: this.loading,
				},
				this.loading ? Spinner.view() : Translate(T.COLLECTION_UPDATE_FORM_PROCEED)
			),
		];
	}

	@action private async proceedWithWarnings() {
		this.setLoading(true);
		try {
			await this.ctrl.updateFormCollections(true);
			NotificationService.success(this.ctx.success);
			this.close();
		} catch (e) {
			Logger.error(e);
			NotificationService.error({ key: T.ERROR_GENERAL });
			this.setLoading(false);
		}
	}
}
